/**
 * Internationalization (i18n) System
 *
 * Simple i18n implementation using React Context for documentation pages.
 * Supports: English, Chinese, Japanese, Korean, Spanish, French, Arabic, Portuguese, Russian, German, Ukrainian, Italian
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

import { isLocale, localizePath, splitLocalePath, type Locale } from './locale-path';
export type { Locale };

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// Translation type
type Translations = Record<string, string>;

// Import translations
import en from '../locales/docs/en';
import zh from '../locales/docs/zh';
import ja from '../locales/docs/ja';
import ko from '../locales/docs/ko';
import es from '../locales/docs/es';
import fr from '../locales/docs/fr';
import ar from '../locales/docs/ar';
import pt from '../locales/docs/pt';
import ru from '../locales/docs/ru';
import de from '../locales/docs/de';
import uk from '../locales/docs/uk';
import it from '../locales/docs/it';

const translations: Record<Locale, Translations> = {
  en,
  zh,
  ja,
  ko,
  es,
  fr,
  ar,
  pt,
  ru,
  de,
  uk,
  it,
};

// Language display names
export const localeNames: Record<Locale, string> = {
  en: 'English',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  es: 'Español',
  fr: 'Français',
  ar: 'العربية',
  pt: 'Português',
  ru: 'Русский',
  de: 'Deutsch',
  uk: 'Українська',
  it: 'Italiano',
};

// Detect browser language
function detectBrowserLocale(): Locale {
  const browserLang = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase();

  if (browserLang.startsWith('zh')) return 'zh';
  if (browserLang.startsWith('ja')) return 'ja';
  if (browserLang.startsWith('ko')) return 'ko';
  if (browserLang.startsWith('es')) return 'es';
  if (browserLang.startsWith('fr')) return 'fr';
  if (browserLang.startsWith('ar')) return 'ar';
  if (browserLang.startsWith('pt')) return 'pt';
  if (browserLang.startsWith('ru')) return 'ru';
  if (browserLang.startsWith('de')) return 'de';
  if (browserLang.startsWith('uk')) return 'uk';
  if (browserLang.startsWith('it')) return 'it';

  return 'en';
}

// Get locale from URL query string (?lang=ja)
function getUrlLocale(): Locale | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (isLocale(lang)) {
      return lang;
    }
  } catch {
    // URLSearchParams not available
  }
  return null;
}

// Get stored locale from localStorage
function getStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem('docs-locale');
    if (isLocale(stored)) {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

// Store locale to localStorage
function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem('docs-locale', locale);
  } catch {
    // localStorage not available
  }
}

interface I18nProviderProps {
  children: ReactNode;
  /** Fixes the starting locale; the prerender passes the page's locale here. */
  initialLocale?: Locale;
}

/** The only right-to-left locale the site ships. */
const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(['ar']);

export function isRtl(locale: Locale): boolean {
  return RTL_LOCALES.has(locale);
}

// Locale from a /ja/... path prefix
function getPathLocale(): Locale | null {
  try {
    return splitLocalePath(window.location.pathname).locale;
  } catch {
    return null;
  }
}

/** What the visitor would pick: their stored choice, else the browser language. */
export function getPreferredLocale(): Locale {
  return getStoredLocale() || detectBrowserLocale();
}

/** One key in one locale, for pages whose language is fixed (the commerce disclosure). */
export function translate(locale: Locale, key: string, fallback?: string): string {
  return translations[locale]?.[key] ?? translations.en?.[key] ?? fallback ?? key;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (initialLocale) return initialLocale;
    // Priority: path prefix > URL param > localStorage > browser language
    const pathLocale = getPathLocale();
    if (pathLocale) return pathLocale;
    const urlLocale = getUrlLocale();
    if (urlLocale) {
      // Also store URL locale to localStorage for consistency
      storeLocale(urlLocale);
      return urlLocale;
    }
    return getPreferredLocale();
  });

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    storeLocale(newLocale);
  }, []);

  // One place sets what the document says about its language, covering both the
  // initial render and every change. `dir` was missing entirely, so Arabic —
  // a supported locale — was being laid out left to right: mirrored punctuation,
  // arrows pointing the wrong way, and controls on the wrong edge.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
  }, [locale]);

  const t = useCallback((key: string, fallback?: string): string => translate(locale, key, fallback), [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export function useLocale(): Locale {
  return useI18n().locale;
}

export function useTranslation() {
  const { t, locale } = useI18n();
  return { t, locale };
}

/** Maps a site path to its URL in the current locale (app routes pass through). */
export function useLocalizedPath(): (to: string) => string {
  const { locale } = useI18n();
  return useCallback((to: string) => localizePath(to, locale), [locale]);
}
